import parentIcon from "@assets/avatar/default/parent-512.png"
import AvatarM from "@common/components/avatar/AvatarM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { isBlockOn, TENANT_BLOCKS } from "@data/utility/tenantBlocks"
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import {
    Animated,
    BackHandler,
    Dimensions,
    Easing,
    Image,
    Platform,
    ScrollView,
    TouchableOpacity,
    View,
} from "react-native"
import { ActivityIndicator, Portal } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

const MAX_SCROLL_HEIGHT = 306

// Normalizes a user object for AvatarM which expects `id` and `display_name`.
// storedTokens have `userId`/`displayName`, currentUser has `id`/`display_name`.
export const formatUserForAvatar = (user) => {
    if (!user) return null
    return {
        ...user,
        id: user.userId ?? user.id,
        display_name: user.displayName ?? user.display_name,
    }
}

const AccountRow = ({
    account,
    isSelected,
    isLoadingAccount,
    isSwitching,
    onPress,
    onSettings,
    closeSheet,
}) => {
    return (
        <TouchableOpacity
            key={account.userId}
            style={{
                flexDirection: "row",
                alignItems: "center",
                padding: 12,
                borderRadius: 12,
                marginBottom: 4,
                backgroundColor: isSelected
                    ? theme.colors.primaryLighter
                    : "transparent",
                opacity:
                    isSwitching && !isLoadingAccount && !isSelected ? 0.5 : 1,
            }}
            onPress={
                isSelected && onSettings
                    ? () => {
                          closeSheet?.()
                          onSettings()
                      }
                    : onPress
            }
            disabled={isSwitching && !isSelected}
        >
            <AvatarM user={formatUserForAvatar(account)} size={40} />
            <View style={{ marginLeft: 12, flex: 1 }}>
                <TextM
                    fontSize={16}
                    fontWeight="Medium"
                    color={theme.colors.primary}
                >
                    {account.displayName}
                </TextM>
                {isSelected && onSettings && (
                    <TextM
                        fontSize={11}
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        style={{
                            textDecorationLine: "underline",
                            marginTop: 2,
                        }}
                    >
                        Mon compte
                    </TextM>
                )}
            </View>
            {isLoadingAccount ? (
                <ActivityIndicator
                    size={20}
                    color={theme.colors.primary}
                    style={{ padding: 8 }}
                />
            ) : (
                isSelected &&
                !isSwitching && (
                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            padding: 8,
                        }}
                    >
                        <TextM
                            fontSize={10}
                            fontWeight="Bold"
                            color={theme.colors.primary}
                            style={{ marginRight: 4 }}
                        >
                            Actif
                        </TextM>
                        <FontAwesome5
                            name="check"
                            size={11}
                            color={theme.colors.primary}
                        />
                    </View>
                )
            )}
        </TouchableOpacity>
    )
}

const DefaultTrigger = ({
    onPress,
    disabled,
    displayUser,
    avatarSize,
    isParentMode,
    style,
}) => (
    <TouchableOpacity
        style={{
            flexDirection: "row",
            alignItems: "center",
            padding: 8,
            ...style,
        }}
        onPress={onPress}
        disabled={disabled}
    >
        <AvatarM user={formatUserForAvatar(displayUser)} size={avatarSize} />
        <View style={{ marginLeft: 6 }}>
            <FontAwesome5
                solid
                name="caret-down"
                size={16}
                color={
                    isParentMode ? theme.colors.purple : theme.colors.primary
                }
            />
        </View>
    </TouchableOpacity>
)

const ParentProfileRow = ({ isActive, onPress, isSwitching }) => {
    return (
        <TouchableOpacity
            style={{
                flexDirection: "row",
                alignItems: "center",
                padding: 12,
                borderRadius: 12,
                backgroundColor: isActive
                    ? theme.colors.purpleLight
                    : "transparent",
                opacity: isSwitching && !isActive ? 0.5 : 1,
            }}
            onPress={onPress}
            disabled={isSwitching || isActive}
            activeOpacity={0.7}
        >
            <View
                style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    overflow: "hidden",
                    borderWidth: isActive ? 2 : 1,
                    borderColor: isActive
                        ? theme.colors.purple
                        : theme.colors.purple + "40",
                }}
            >
                <Image
                    source={parentIcon}
                    style={{ width: 40, height: 40 }}
                    resizeMode="cover"
                />
            </View>
            <View style={{ marginLeft: 12, flex: 1 }}>
                <TextM
                    fontSize={16}
                    fontWeight="SemiBold"
                    color={theme.colors.purple}
                >
                    Espace parent
                </TextM>
            </View>
            {isActive ? (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        padding: 8,
                    }}
                >
                    <TextM
                        fontSize={10}
                        fontWeight="Bold"
                        color={theme.colors.purple}
                        style={{ marginRight: 4 }}
                    >
                        Actif
                    </TextM>
                    <FontAwesome5
                        name="check"
                        size={14}
                        color={theme.colors.purple}
                    />
                </View>
            ) : null}
        </TouchableOpacity>
    )
}

const ProfileSwitcherM = (props) => {
    const {
        accounts = [],
        selectedAccount = null,
        onAccountSelect = () => {},
        onParentSelect = () => {},
        isParentMode = false,
        user = null,
        avatarSize = 40,
        disabled = false,
        style = {},
        onSettings = null,
        renderTrigger = null,
        switchingAccountId = null,
        loading = false,
    } = props

    const isSwitching = switchingAccountId !== null || loading
    const dimensions = useContext(DimensionsContext)
    const [visible, setVisible] = useState(false)
    const backdropAnim = useRef(new Animated.Value(0)).current
    const slideAnim = useRef(new Animated.Value(1)).current

    const sheetEasing = Easing.out(Easing.exp)

    const openSheet = useCallback(() => {
        setVisible(true)
        Animated.parallel([
            Animated.timing(backdropAnim, {
                toValue: 1,
                duration: 250,
                easing: sheetEasing,
                useNativeDriver: true,
            }),
            Animated.timing(slideAnim, {
                toValue: 0,
                duration: 250,
                easing: sheetEasing,
                useNativeDriver: true,
            }),
        ]).start()
    }, [backdropAnim, slideAnim])

    const closeSheet = useCallback(() => {
        Animated.parallel([
            Animated.timing(backdropAnim, {
                toValue: 0,
                duration: 250,
                easing: sheetEasing,
                useNativeDriver: true,
            }),
            Animated.timing(slideAnim, {
                toValue: 1,
                duration: 250,
                easing: sheetEasing,
                useNativeDriver: true,
            }),
        ]).start(() => setVisible(false))
    }, [backdropAnim, slideAnim])

    // Handle Android back button (replaces Modal's onRequestClose)
    useEffect(() => {
        if (!visible || Platform.OS === "web") return
        const handler = BackHandler.addEventListener(
            "hardwareBackPress",
            () => {
                closeSheet()
                return true
            }
        )
        return () => handler.remove()
    }, [visible, closeSheet])

    const handleAccountSelection = (account) => {
        if (isSwitching) return
        closeSheet()
        onAccountSelect(account)
    }

    const handleParentSelect = () => {
        if (isSwitching) return
        closeSheet()
        onParentSelect()
    }

    const displayUser = selectedAccount || user

    if (accounts.length === 0) return null

    const selectedId = selectedAccount?.userId

    const renderAccountRow = (account) => {
        const isSelected = !isParentMode && account.userId === selectedId

        return (
            <AccountRow
                key={account.userId}
                account={account}
                isSelected={isSelected}
                isLoadingAccount={
                    !isParentMode && switchingAccountId === account.userId
                }
                isSwitching={isSwitching}
                onPress={() => handleAccountSelection(account)}
                onSettings={isSelected ? onSettings : null}
                closeSheet={isSelected ? closeSheet : null}
            />
        )
    }

    const sheetContent = (
        <View
            style={{
                backgroundColor: theme.colors.white,
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                overflow: "hidden",
                width: dimensions?.width,
            }}
        >
            <View
                style={{
                    alignSelf: "center",
                    width: (7.5 * Dimensions.get("window").width) / 100,
                    height: 4,
                    borderRadius: 4,
                    backgroundColor: "rgba(0, 0, 0, 0.75)",
                    marginTop: 10,
                }}
            />
            <View style={{ height: 8 }} />

            <ScrollView
                style={{
                    maxHeight: MAX_SCROLL_HEIGHT,
                    paddingHorizontal: 12,
                }}
            >
                {isParentMode && (
                    <ParentProfileRow
                        isActive
                        onPress={handleParentSelect}
                        isSwitching={isSwitching}
                    />
                )}
                {[...accounts]
                    .sort((a, b) => {
                        if (a.userId === selectedId) return -1
                        if (b.userId === selectedId) return 1
                        return 0
                    })
                    .map(renderAccountRow)}
                {/* Pas d'espace parent dans l'espace (tenant) de l'élève : pas de ligne. */}
                {!isParentMode && isBlockOn(user, TENANT_BLOCKS.PARENTS) && (
                    <ParentProfileRow
                        isActive={false}
                        onPress={handleParentSelect}
                        isSwitching={isSwitching}
                    />
                )}
            </ScrollView>
            <View style={{ height: 20 }} />
        </View>
    )

    const trigger = renderTrigger ? (
        renderTrigger({
            onPress: openSheet,
            formattedUser: formatUserForAvatar(displayUser),
        })
    ) : (
        <DefaultTrigger
            onPress={openSheet}
            disabled={disabled}
            displayUser={displayUser}
            avatarSize={avatarSize}
            isParentMode={isParentMode}
            style={style}
        />
    )

    return (
        <>
            {trigger}
            {visible && (
                <Portal>
                    <View
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            justifyContent: "flex-end",
                            alignItems: "center",
                        }}
                    >
                        <Animated.View
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: theme.colors.blackTransparent2,
                                opacity: backdropAnim,
                            }}
                        >
                            <TouchableOpacity
                                style={{ flex: 1 }}
                                activeOpacity={1}
                                onPress={closeSheet}
                            />
                        </Animated.View>
                        <Animated.View
                            style={{
                                transform: [
                                    {
                                        translateY: slideAnim.interpolate({
                                            inputRange: [0, 1],
                                            outputRange: [0, 400],
                                        }),
                                    },
                                ],
                            }}
                        >
                            {sheetContent}
                        </Animated.View>
                    </View>
                </Portal>
            )}
        </>
    )
}

export default ProfileSwitcherM
